import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { Alert } from './Alert'
import { Badge } from './Badge'
import { Button } from './Button'
import { Card, CardHeading } from './Card'
import { ConfirmDialog } from './ConfirmDialog'
import { EmptyState } from './EmptyState'
import { Form } from './Form'
import { usePageLeaveGuard, useSubmitLock } from './form-guards'
import { Input } from './Input'
import { KeyRound } from './icons'
import { Loading } from './Loading'
import { PageHeader } from './PageHeader'
import { Select } from './Select'
import { Table, type TableColumn } from './Table'

type KeyRecord = {
  id: string
  name: string
  scope: string
  expiresAt: string | null
  createdAt: string
  lastUsedAt: string | null
  displayKey?: string
  purpose?: string
  revokedAt?: string | null
  status?: string
}
type CreateKey = { name: string; scope: string; purpose?: string; expiresInDays?: number }
type DescribeError = (error: unknown, fallback: string) => string
const rawError: DescribeError = (error, fallback) => error instanceof Error ? error.message : fallback
const defaultExpiries = [{ value: '', label: '永久' }, { value: '90', label: '90 天' }, { value: '365', label: '365 天' }]
type IssuedKey = { id?: string; name: string; token: string }

function CopyToken({ value }: { value: string }) {
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState(false)
  const resetTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(() => () => clearTimeout(resetTimer.current), [])
  return <>
    <Button type="button" variant="secondary" size="sm" onClick={async () => {
      try {
        await navigator.clipboard.writeText(value)
        setCopied(true)
        setError(false)
        clearTimeout(resetTimer.current)
        resetTimer.current = setTimeout(() => setCopied(false), 1600)
      } catch {
        setCopied(false)
        setError(true)
      }
    }}>{copied ? '已复制' : '复制'}</Button>
    {error ? <span role="status">复制失败，请手动复制密钥。</span> : null}
  </>
}

export function AgentAccessPage({ load, create, revoke, formatTime, scopes, purposes, expiryOptions = defaultExpiries, defaultExpiry, nameMaxLength = 50, singleKey = false, introduction, description, cliPanel, additionalContent, issuedNotice, issuedActions, describeError = rawError }: {
  load: (offset?: number) => Promise<{ items: KeyRecord[]; nextOffset?: number | null }>
  create: (input: CreateKey) => Promise<{ token: string; record: { id?: string; name: string } }>
  revoke: (id: string) => Promise<unknown>
  formatTime: (value: string) => string
  scopes: readonly { value: string; label: string }[]
  purposes?: readonly { value: string; label: string }[]
  expiryOptions?: readonly { value: string; label: string }[]
  defaultExpiry?: string
  nameMaxLength?: number | null
  singleKey?: boolean
  introduction?: ReactNode
  description: string
  cliPanel: ReactNode | ((keys: readonly KeyRecord[], reload: () => Promise<void>) => ReactNode)
  additionalContent?: ReactNode | ((keys: readonly KeyRecord[]) => ReactNode)
  issuedNotice?: ReactNode
  issuedActions?: (issued: IssuedKey) => ReactNode
  describeError?: DescribeError
}) {
  const formId = useId()
  const nameRef = useRef<HTMLInputElement>(null)
  const [keys, setKeys] = useState<KeyRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [scope, setScope] = useState(scopes[0]?.value ?? '')
  const [purpose, setPurpose] = useState(purposes?.[0]?.value ?? '')
  const [expiresInDays, setExpiresInDays] = useState(defaultExpiry ?? expiryOptions[0]?.value ?? '')
  const [resetKey, setResetKey] = useState(0)
  const [dirty, setDirty] = useState(false)
  const [creating, setCreating] = useState(false)
  const [issued, setIssued] = useState<IssuedKey | null>(null)
  const [nextOffset, setNextOffset] = useState<number | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)
  const [moreError, setMoreError] = useState<string | null>(null)
  const [revoking, setRevoking] = useState<KeyRecord | null>(null)
  const [revokeError, setRevokeError] = useState<string | null>(null)
  const { pending: revokePending, run: runRevoke } = useSubmitLock()
  const { dialog: leaveDialog } = usePageLeaveGuard(dirty)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    setMoreError(null)
    try {
      const result = await load()
      setKeys(result.items)
      setNextOffset(result.nextOffset ?? null)
    }
    catch (err) { setError(describeError(err, '加载失败')) }
    finally { setLoading(false) }
  }, [load, describeError])
  useEffect(() => { void reload() }, [reload])
  const loadMore = async () => {
    if (nextOffset === null || loadingMore) return
    setLoadingMore(true)
    setMoreError(null)
    try {
      const result = await load(nextOffset)
      setKeys((current) => [...current, ...result.items])
      setNextOffset(result.nextOffset ?? null)
    } catch (err) { setMoreError(describeError(err, '更多密钥读取失败')) }
    finally { setLoadingMore(false) }
  }

  const submit = async () => {
    if (!singleKey && !name.trim()) return { ok: false, error: '请输入密钥名称' }
    try {
      const data = await create({ name: singleKey ? 'Agent CLI' : name.trim(), scope, ...(purposes ? { purpose } : {}), ...(expiresInDays ? { expiresInDays: Number(expiresInDays) } : {}) })
      setIssued({ id: data.record.id, name: data.record.name, token: data.token })
      setName('')
      setDirty(false)
      setResetKey((current) => current + 1)
      await reload()
    } catch (err) {
      return { ok: false, error: describeError(err, '创建失败') }
    }
  }
  const confirmRevoke = () => runRevoke(async () => {
    if (!revoking) return
    const revokedId = revoking.id
    setRevokeError(null)
    try {
      await revoke(revokedId)
      setIssued((current) => current?.id === revokedId ? null : current)
      setRevoking(null)
      await reload()
    } catch (err) { setRevokeError(describeError(err, '吊销失败')) }
  })
  const keyStatus = (key: KeyRecord) => key.revokedAt || key.status === 'revoked' ? '已吊销'
    : key.status === 'expired' || key.expiresAt !== null && new Date(key.expiresAt).getTime() <= Date.now() ? '已过期' : '有效'
  const expiryColumn: TableColumn<KeyRecord> = { key: 'expiresAt', title: '到期时间', render: (key) => <span className="text-muted">{key.expiresAt ? formatTime(key.expiresAt) : '永久'}</span> }
  const columns: TableColumn<KeyRecord>[] = [
    { key: 'name', title: '名称', render: (key) => key.name },
    { key: 'id', title: '标识', render: (key) => <span className="font-mono text-muted">{key.displayKey ?? key.id}</span> },
    ...(purposes ? [{ key: 'purpose', title: '用途', render: (key: KeyRecord) => purposes.find((item) => item.value === key.purpose)?.label ?? key.purpose }] : []),
    ...(scopes.length ? [{ key: 'scope', title: purposes ? '作用域' : '权限', render: (key: KeyRecord) => scopes.find((item) => item.value === key.scope)?.label ?? key.scope }] : []),
    ...(purposes ? [expiryColumn] : []),
    { key: 'status', title: '状态', render: (key) => {
      const status = keyStatus(key)
      return <Badge tone={status === '有效' ? 'success' : 'neutral'}>{status}</Badge>
    } },
    { key: 'createdAt', title: '创建时间', render: (key) => <span className="text-muted">{formatTime(key.createdAt)}</span> },
    ...(!purposes ? [expiryColumn] : []),
    { key: 'lastUsedAt', title: '最近使用', render: (key) => <span className="text-muted">{key.lastUsedAt ? formatTime(key.lastUsedAt) : '从未使用'}</span> },
    { key: 'action', title: '操作', render: (key) => keyStatus(key) === '有效' ? <Button type="button" variant="ghostDanger" size="sm" onClick={() => {
      setRevokeError(null)
      setRevoking(key)
    }}>吊销</Button> : <span className="text-muted">—</span> }
  ]
  return <div className="flex w-full flex-col gap-4 pb-20 md:pb-0">
    <PageHeader eyebrow="工具" title="接入 Agent" description={description}
      primaryAction={{ label: singleKey ? (keys.length ? '轮换密钥' : '生成个人密钥') : '创建密钥', type: 'submit', form: formId, pending: creating, disabled: singleKey && (loading || error !== null) }} />
    {introduction}
    <Form id={formId} resetKey={resetKey} onPendingChange={setCreating} onSubmit={submit}
      className="flex flex-wrap items-center gap-2" onChangeCapture={() => setDirty(true)}>
      {!singleKey ? <Input ref={nameRef} className="w-52 max-w-full" aria-label="密钥名称" placeholder="密钥名称（如：我的 Codex）" maxLength={nameMaxLength ?? undefined} value={name} onChange={(event) => setName(event.target.value)} /> : null}
      {!singleKey && !name.trim() ? <span className="text-xs text-muted">填写一个便于识别的用途名称。</span> : null}
      {purposes ? <Select className="w-28" aria-label="用途" value={purpose} onChange={(event) => setPurpose(event.target.value)}>
        {purposes.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
      </Select> : null}
      {scopes.length ? <Select className="w-28" aria-label="权限范围" value={scope} onChange={(event) => setScope(event.target.value)}>
        {scopes.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
      </Select> : null}
      <Select className="w-28" aria-label="有效期" value={expiresInDays} onChange={(event) => setExpiresInDays(event.target.value)}>
        {expiryOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </Select>
      {expiresInDays === '' ? <span className="text-xs text-muted">永久有效。密钥泄露或不再使用时请在本页吊销</span> : null}
    </Form>
    {typeof cliPanel === 'function' ? cliPanel(keys, reload) : cliPanel}
    {typeof additionalContent === 'function' ? additionalContent(keys) : additionalContent}
    {issued ? <Alert tone="success">
      <strong>密钥「{issued.name}」只显示这一次，请立即复制保存。</strong>
      {issuedNotice ?? '遗失后只能吊销并重新创建；不要把它发给他人或提交到代码库。'}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <code className="select-text break-all rounded-[var(--radius-lg)] bg-paper px-3 py-1.5 font-mono text-xs">{issued.token}</code>
        <CopyToken key={issued.token} value={issued.token} />
        {issuedActions?.(issued)}
        <Button type="button" variant="ghost" size="sm" aria-label="关闭密钥明文" onClick={() => setIssued(null)}>关闭</Button>
      </div>
    </Alert> : null}
    <Card>
      <CardHeading title="我的密钥" />
      {loading ? <Loading /> : error ? <Alert action={<Button type="button" variant="secondary" size="sm" onClick={() => void reload()}>重试</Button>}>{error}</Alert> : keys.length === 0 ?
        <EmptyState title="还没有 API 密钥" description="用上方表单创建第一个密钥；明文只在创建时显示一次。" icon={KeyRound}
          action={singleKey ? undefined : <Button type="button" variant="secondary" size="sm" onClick={() => {
            nameRef.current?.focus({ preventScroll: true })
            nameRef.current?.scrollIntoView({ block: 'center' })
          }}>填写密钥名称</Button>} /> : singleKey ? <div className="flex flex-col gap-3 px-5 py-4">
          <code>{keys[0]!.displayKey ?? keys[0]!.id}</code>
          <dl className="m-0 grid grid-cols-2 gap-2 text-xs">
            <div><dt>创建时间</dt><dd className="m-0">{formatTime(keys[0]!.createdAt)}</dd></div>
            <div><dt>最近使用</dt><dd className="m-0">{keys[0]!.lastUsedAt ? formatTime(keys[0]!.lastUsedAt) : '尚未使用'}</dd></div>
            <div><dt>到期时间</dt><dd className="m-0">{keys[0]!.expiresAt ? formatTime(keys[0]!.expiresAt) : '永久'}</dd></div>
          </dl>
          <Badge tone={keyStatus(keys[0]!) === '有效' ? 'success' : 'neutral'}>{keyStatus(keys[0]!)}</Badge>
          <span className="text-sm">{purposes?.find((item) => item.value === keys[0]!.purpose)?.label}</span>
          <Alert tone="warning">轮换后旧密钥立即失效。请同步更新所有使用它的 Agent。</Alert>
          <div><Button type="button" variant="ghostDanger" size="sm" onClick={() => { setRevokeError(null); setRevoking(keys[0]!) }}>撤销密钥</Button></div>
        </div> :
        <Table ariaLabel="我的 API 密钥" columns={columns} rows={keys} rowKey={(key) => key.id} />}
      {moreError ? <Alert>{moreError}</Alert> : null}
      {nextOffset !== null ? <div className="flex justify-center border-t border-line px-5 py-3"><Button type="button" variant="secondary" loading={loadingMore} onClick={() => void loadMore()}>加载更多密钥</Button></div> : null}
    </Card>
    <ConfirmDialog open={revoking !== null} title={singleKey ? '撤销 API 密钥？' : `确认吊销密钥「${revoking?.name ?? ''}」？`}
      description={<><p>{singleKey ? '使用这枚密钥的所有 CLI 和 Agent 会立即停止工作。' : '吊销后无法恢复，使用它的 Agent 会立即失去访问权限。'}</p>{revokeError ? <Alert>{revokeError}</Alert> : null}</>}
      confirmText={singleKey ? '确认撤销' : '确认吊销'} danger loading={revokePending} onConfirm={() => void confirmRevoke()} onClose={() => setRevoking(null)} />
    {leaveDialog}
  </div>
}

export function AgentCliDownloadPanel({ packageName, commands, downloadHref, downloadSkill, entryPoint, description, help, describeError = rawError }: {
  packageName: string
  commands: string
  downloadHref: string
  downloadSkill: (expiresInDays?: number) => Promise<Blob>
  entryPoint?: string
  description?: ReactNode
  help?: ReactNode
  describeError?: DescribeError
}) {
  const [expiresInDays, setExpiresInDays] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const { pending, run } = useSubmitLock()
  const download = () => run(async () => {
    setMessage(null)
    try {
      const blob = await downloadSkill(expiresInDays ? Number(expiresInDays) : undefined)
      const url = URL.createObjectURL(blob)
      try {
        const link = document.createElement('a')
        link.href = url
        link.download = `${packageName}-skill-private.zip`
        link.click()
      } finally { URL.revokeObjectURL(url) }
    } catch (err) { setMessage(describeError(err, '预配置包下载失败')) }
  })
  return <Card>
    <CardHeading title="安装与配置 Agent CLI" description="让 Agent 通过统一 Agent API 操作你有权限访问的数据" />
    <div className="flex flex-col gap-3 px-5 py-4 text-sm text-ink">
      <p className="m-0">本应用已接入<span className="font-medium">统一 Agent API</span>{entryPoint ? <>：单一入口 <code>POST {entryPoint}</code></> : null}。密钥权限不会超过你的账号权限。{description}</p>
      <div className="flex flex-wrap gap-2">
        <a className="rounded-[var(--radius-md)] border border-line bg-paper px-3.5 py-1.5 text-sm text-ink no-underline hover:bg-soft" href={downloadHref} download={`${packageName}-cli.zip`}>下载 CLI 安装包</a>
        <Button type="button" variant="secondary" size="sm" loading={pending} onClick={() => void download()}>下载预配置 SKILL</Button>
        <Select className="w-28" aria-label="预配置密钥有效期" value={expiresInDays} onChange={(event) => setExpiresInDays(event.target.value)}>
          <option value="">永久</option><option value="90">90 天</option><option value="365">365 天</option>
        </Select>
      </div>
      {message ? <Alert>{message}</Alert> : null}
      <pre className="m-0 overflow-x-auto rounded-[var(--radius-lg)] bg-soft p-3 text-xs text-muted"><code>{commands}</code></pre>
      <p className="m-0 text-xs text-muted">密钥只走 stdin 或预配置包，不要把密钥写进命令行。{help ?? '步骤见 docs/agent-access.md。'}</p>
    </div>
  </Card>
}
